import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useDropdownAnchor } from './useDropdownAnchor';
import { FLOW_PORTAL_PROPS } from '../useClickOutside';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';
import { appendChips, CHIP_SEPARATOR } from './split-chip-input';

/**
 * Free-entry chip input for `in_any_of` filter conditions (plan U8).
 * Chips-in-field: Enter or a comma commits the current draft as a chip
 * (a comma-separated draft, or a pasted comma/newline/tab-separated list,
 * becomes one chip per item — see `split-chip-input.ts`; semicolons do not
 * split), Backspace on an empty draft pops the last chip. Modeled on
 * `mini-apps/_shared/components/TableFilterBuilder.tsx`'s
 * `PicklistMultiSelect` and launcher's `WaterfallConfig.tsx`'s
 * `ContainsAnyInput` — Flow's own precedent for chip-style multi-value
 * inputs.
 *
 * The optional `options` suggestion dropdown, though, is portaled via
 * `useDropdownAnchor` / `useAnchoredPosition` / `FLOW_PORTAL_PROPS` — the
 * same machinery `FieldPicker`/`PersonValuePicker` use — rather than either
 * precedent's naive `position: absolute` panel: this control lives inside
 * `FilterPopover`, itself typically hosted in a `ResponsivePopover`, and a
 * plain absolute-positioned dropdown's outside-click check has no idea
 * about the popover's own boundary — clicking a suggestion would register
 * as an "outside click" and self-close the WHOLE filters popover before the
 * click handler ever fires.
 *
 * `options` is fed from a `FilterFieldCatalogEntry.values` via `valueOptionsOf`
 * (U7 — the tracker's Status field is the first user: `filter-fields.ts`'s
 * `TRACKER_FILTER_FIELDS`) when the field has enumerated values; when
 * omitted (every other field type `in_any_of` supports today), this is pure
 * free-text chip entry with no suggestion dropdown at all.
 *
 * Labeled options (plan 001 A1 — e.g. the Plays preset's "Play" field,
 * stored value a campaign/flow record id, displayed value the play NAME):
 * `options` is `{ value, label }` pairs. Suggestions filter and render by
 * `label`; selecting one commits `value` (never the label) into `value[]`.
 * `value[]` itself always holds stored values — chip rendering looks each
 * one up in `options` and shows the matching `label`, falling back to the
 * raw stored value when no option matches (free-typed chips, or a field
 * with no `options` at all).
 */
export interface MultiValueInputProps {
  value: string[];
  options?: Array<{ value: string; label: string }>;
  onChange: (value: string[]) => void;
}

const CHIP_CLASS =
  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-bg-secondary dark:bg-nightshade-700 text-content-primary dark:text-nightshade-100 text-[11px]';

export function MultiValueInput({ value, options, onChange }: MultiValueInputProps) {
  const [draft, setDraft] = useState('');
  const { open, setOpen, triggerRef, menuRef, menuStyle } = useDropdownAnchor();
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions = useMemo(() => {
    if (!options) return [];
    const q = draft.trim().toLowerCase();
    return options.filter(o => !value.includes(o.value) && (!q || o.label.toLowerCase().includes(q)));
  }, [options, value, draft]);

  // `label` display for a stored chip value: the matching option's label
  // when one exists, else the raw stored value (free-typed chip, or no
  // `options` supplied at all).
  const labelFor = (chip: string): string => options?.find(o => o.value === chip)?.label ?? chip;

  const addChips = (next: string[]) => {
    if (next !== value) onChange(next);
    setDraft('');
  };

  const commit = (raw: string) => addChips(appendChips(value, raw));

  const removeChip = (chip: string) => onChange(value.filter(v => v !== chip));

  // Refs holding the LATEST draft/value/onChange, so the unmount-flush
  // effect below always commits what's currently on screen, not a stale
  // closure from whenever the effect last ran.
  const draftRef = useRef(draft);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  draftRef.current = draft;
  valueRef.current = value;
  onChangeRef.current = onChange;

  // Unmount flush: commits an in-progress draft chip that never got a
  // blur/Enter/comma — e.g. the popover unmounts (escape-stack close, an
  // outside click, a parent re-render) while this input is still focused,
  // which fires no blur event and would otherwise drop the typed text
  // silently. StrictMode-safe: this effect's cleanup only fires on real
  // unmount (not the dev-mode setup→cleanup→setup double-invoke) because it
  // reads refs at cleanup time, not effect-setup time — there is no
  // mount-time state to reset here (contrast with lifetime-state refs like
  // `cancelled`/`initialized`, which DO need a reset at the top of the
  // effect).
  useEffect(() => {
    return () => {
      const next = appendChips(valueRef.current, draftRef.current);
      if (next !== valueRef.current) onChangeRef.current(next);
    };
  }, []);

  return (
    <div ref={triggerRef} className="relative">
      <div
        className="w-full flex flex-wrap items-center gap-1 px-1.5 py-1 min-h-[26px] text-[11.5px] border border-secondary dark:border-nightshade-700 rounded-md bg-white dark:bg-nightshade-800 cursor-text focus-within:ring-1 focus-within:ring-content-action"
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
      >
        {value.map(chip => (
          <span key={chip} className={CHIP_CLASS}>
            {labelFor(chip)}
            <button
              type="button"
              onClick={e => { e.stopPropagation(); removeChip(chip); }}
              className="text-content-tertiary dark:text-nightshade-500 hover:text-content-danger dark:hover:text-red-400"
              title={`Remove ${chip}`}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        <input
          {...AUTOFILL_IGNORE_PROPS}
          ref={inputRef}
          type="text"
          value={draft}
          onChange={e => { setDraft(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          // Blur commits the pending chip draft: this control's OWN local
          // "typed but not yet Enter'd" text, independent of FilterPopover's
          // staged filter draft (QA-2) — without it a rep who types a chip
          // value and clicks away loses it before it ever becomes a chip in
          // the (still-staged) condition value. Suggestion clicks use
          // `onMouseDown` + `preventDefault`, so this never double-commits one.
          onBlur={() => { if (draft.trim()) commit(draft); }}
          onPaste={e => {
            const text = e.clipboardData.getData('text/plain');
            // No separator: native paste (caret, undo stack) is fine.
            if (!CHIP_SEPARATOR.test(text)) return;
            e.preventDefault();
            const el = e.currentTarget;
            const start = el.selectionStart ?? draft.length;
            const end = el.selectionEnd ?? start;
            commit(draft.slice(0, start) + text + draft.slice(end));
          }}
          onKeyDown={e => {
            if (e.key === ',') {
              // A comma never enters the draft (so a leading one can't
              // produce a stray empty chip); it only commits real text.
              e.preventDefault();
              if (draft.trim()) commit(draft);
            } else if (e.key === 'Enter' && draft.trim()) {
              e.preventDefault();
              commit(draft);
            } else if (e.key === 'Backspace' && !draft && value.length > 0) {
              removeChip(value[value.length - 1]);
            }
          }}
          placeholder={value.length === 0 ? 'Type a value, Enter to add…' : ''}
          className="flex-1 min-w-[60px] bg-transparent outline-none text-content-primary dark:text-nightshade-100 placeholder:text-content-placeholder"
        />
      </div>
      {open && suggestions.length > 0 && createPortal(
        <div
          ref={menuRef}
          data-testid="multi-value-suggestions"
          {...FLOW_PORTAL_PROPS}
          style={menuStyle}
          className="w-[220px] max-h-[220px] overflow-auto rounded-lg border border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-800 shadow-lg py-1"
        >
          {suggestions.map(s => (
            <button
              key={s.value}
              type="button"
              onMouseDown={e => {
                e.preventDefault();
                // Bypass splitting: an option value may itself contain a comma.
                addChips(value.includes(s.value) ? value : [...value, s.value]);
              }}
              className="block w-full text-left px-2.5 py-1 text-[12px] text-content-primary dark:text-nightshade-100 hover:bg-bg-secondary dark:hover:bg-nightshade-700"
            >
              {s.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
